import type { MarketAccent, MarketDeal } from '../types/market'

/**
 * The marketplace's visual assets and merchandising, kept apart from the
 * inventory itself so imagery and campaigns can change without touching the
 * listings they sit beside.
 */

export const marketHeroImage =
  'https://cdn.magicpatterns.com/patterns/generated-images/d5f01d77-364f-48a8-841b-183efc1c0ca6.jpg'

/**
 * Category accent tokens. Only the icon tile is tinted — the card itself stays
 * on the shared surface so nine categories remain distinguishable without the
 * page becoming a rainbow.
 */
export const ACCENT_TILE: Record<MarketAccent, string> = {
  amber: 'bg-[#fef0e2] text-[#b45309]',
  green: 'bg-[#e3f3ee] text-[#0f766e]',
  blue: 'bg-[#e5eefb] text-[#1d4ed8]',
  violet: 'bg-[#efe9fb] text-[#6d28d9]',
  orange: 'bg-[#fdeede] text-[#c2410c]',
  teal: 'bg-[#dff2f1] text-[#0e7490]',
  pink: 'bg-[#fce8ef] text-[#be185d]',
  indigo: 'bg-[#e7eafc] text-[#3730a3]',
  purple: 'bg-[#f2e9fa] text-[#7e22ce]',
}

/** A very light wash behind the whole card, one step quieter than the tile. */
export const ACCENT_CARD: Record<MarketAccent, string> = {
  amber: 'hover:bg-[#fffaf4]',
  green: 'hover:bg-[#f5fbf9]',
  blue: 'hover:bg-[#f6f9fe]',
  violet: 'hover:bg-[#faf8fe]',
  orange: 'hover:bg-[#fffaf5]',
  teal: 'hover:bg-[#f5fbfb]',
  pink: 'hover:bg-[#fef8fa]',
  indigo: 'hover:bg-[#f8f9fe]',
  purple: 'hover:bg-[#fcf8fe]',
}

/** Provider storefront imagery, keyed by seller id. */
export const sellerImages: Record<string, string> = {
  'sel-1': 'https://cdn.magicpatterns.com/patterns/generated-images/9b21f1e5-386c-4d25-a5f2-997da76a7eb5.jpg',
  'sel-2': 'https://cdn.magicpatterns.com/patterns/generated-images/5a0d110d-984e-4829-b2a5-e9e91edc4f9a.jpg',
  'sel-3': 'https://cdn.magicpatterns.com/patterns/generated-images/b55dbb6f-060e-48e9-9703-18980db04e9f.jpg',
  'sel-4': 'https://cdn.magicpatterns.com/patterns/generated-images/cc4f1353-677b-4af8-a1e5-e75fed474889.jpg',
  'sel-6': 'https://cdn.magicpatterns.com/patterns/generated-images/a9adc999-6422-44e0-8c3e-3e078b883936.jpg',
  'sel-7': 'https://cdn.magicpatterns.com/patterns/generated-images/ee42cb22-f5ab-44e5-a22f-ba042e0ddbbd.jpg',
}

export const providerCtaImage =
  'https://cdn.magicpatterns.com/patterns/generated-images/ae103170-dfec-462f-a7f2-28754ef6e97d.jpg'

/**
 * Campaigns. Each carries an end date, because a promotion with no stated end
 * is just decoration and cannot be held to.
 */
export const marketDeals: MarketDeal[] = [
  {
    id: 'deal-meals',
    title: 'Student meal deals',
    detail: 'Up to 25% off selected meals from verified campus vendors.',
    cta: 'Order now',
    to: '/market/food',
    imageUrl:
      'https://cdn.magicpatterns.com/patterns/generated-images/e568cd19-302a-4f42-97dd-06e5288a7133.jpg',
    endsLabel: 'Ends 31 August',
  },
  {
    id: 'deal-books',
    title: 'Back to school',
    detail: 'Up to 20% off textbooks, past questions and stationery.',
    cta: 'Shop now',
    to: '/market/books',
    imageUrl:
      'https://cdn.magicpatterns.com/patterns/generated-images/27dbf14f-4fe7-417b-b236-83c2ddba60bd.jpg',
    endsLabel: 'Ends 12 September',
  },
  {
    id: 'deal-services',
    title: 'Service discounts',
    detail: 'Quality services at student-friendly prices, held in escrow.',
    cta: 'Explore now',
    to: '/market/services',
    imageUrl:
      'https://cdn.magicpatterns.com/patterns/generated-images/a9adc999-6422-44e0-8c3e-3e078b883936.jpg',
    endsLabel: 'Ongoing this semester',
  },
]

/** What Campuzon will and will not stand behind, stated in four lines. */
export const trustPoints = [
  {
    id: 'verified',
    label: 'Verified and inspected',
    detail: 'We check who a provider is. Uninspected sellers say so on every listing.',
  },
  {
    id: 'student',
    label: 'Student to student',
    detail: 'Built around your campus, and mostly sold by the people on it.',
  },
  {
    id: 'payments',
    label: 'Payment held, not passed on',
    detail: 'Service money is released when you accept the work, not when you pay.',
  },
  {
    id: 'support',
    label: 'A person reviews reports',
    detail: 'Every listing, seller and order carries a report control that reaches a human.',
  },
] as const
